/**
 * App-wide constants.
 *
 * When @iwa2/shared exposes region data/enums, re-export from there.
 * For now we define the canonical Lebanese regions from spec 11 directly.
 */

import type { Region, Sect, HousingType, SeekerRelation } from '@/types';

export const LANG_STORAGE_KEY = 'iwa2_lang';
export const FILTERS_STORAGE_KEY = 'iwa2_filters';

export const API_BASE = '/api';
export const MEDIA_BASE = '/media';

export const OTP_LENGTH = 6;
export const OTP_RESEND_SECONDS = 60;
export const PHONE_COUNTRY_CODE = '+961';

export const MAX_PHOTOS_PER_LISTING = 10;
export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export const REGIONS: Region[] = [
  // Beirut
  { id: 'beirut', ar: 'بيروت', en: 'Beirut', governorate: 'beirut' },
  // Mount Lebanon
  { id: 'metn', ar: 'المتن', en: 'Metn', governorate: 'mount_lebanon' },
  { id: 'keserwan', ar: 'كسروان', en: 'Keserwan', governorate: 'mount_lebanon' },
  { id: 'jbeil', ar: 'جبيل', en: 'Jbeil', governorate: 'mount_lebanon' },
  { id: 'baabda', ar: 'بعبدا', en: 'Baabda', governorate: 'mount_lebanon' },
  { id: 'aley', ar: 'عاليه', en: 'Aley', governorate: 'mount_lebanon' },
  { id: 'chouf', ar: 'الشوف', en: 'Chouf', governorate: 'mount_lebanon' },
  // North
  { id: 'tripoli', ar: 'طرابلس', en: 'Tripoli', governorate: 'north' },
  { id: 'minieh_danniyeh', ar: 'المنية-الضنية', en: 'Minieh-Danniyeh', governorate: 'north' },
  { id: 'zgharta', ar: 'زغرتا', en: 'Zgharta', governorate: 'north' },
  { id: 'bcharre', ar: 'بشري', en: 'Bcharre', governorate: 'north' },
  { id: 'koura', ar: 'الكورة', en: 'Koura', governorate: 'north' },
  { id: 'batroun', ar: 'البترون', en: 'Batroun', governorate: 'north' },
  // Akkar
  { id: 'akkar', ar: 'عكار', en: 'Akkar', governorate: 'akkar' },
  // Bekaa
  { id: 'zahle', ar: 'زحلة', en: 'Zahle', governorate: 'bekaa' },
  { id: 'west_bekaa_rashaya', ar: 'البقاع الغربي-راشيا', en: 'West Bekaa-Rashaya', governorate: 'bekaa' },
  // Baalbek-Hermel
  { id: 'baalbek', ar: 'بعلبك', en: 'Baalbek', governorate: 'baalbek_hermel' },
  { id: 'hermel', ar: 'الهرمل', en: 'Hermel', governorate: 'baalbek_hermel' },
  // South
  { id: 'saida', ar: 'صيدا', en: 'Saida', governorate: 'south' },
  { id: 'jezzine', ar: 'جزين', en: 'Jezzine', governorate: 'south' },
  { id: 'tyre', ar: 'صور', en: 'Tyre', governorate: 'south' },
  // Nabatieh
  { id: 'nabatieh', ar: 'النبطية', en: 'Nabatieh', governorate: 'nabatieh' },
  { id: 'hasbaya', ar: 'حاصبيا', en: 'Hasbaya', governorate: 'nabatieh' },
  { id: 'marjayoun', ar: 'مرجعيون', en: 'Marjayoun', governorate: 'nabatieh' },
  { id: 'bint_jbeil', ar: 'بنت جبيل', en: 'Bint Jbeil', governorate: 'nabatieh' },
];

export const GOVERNORATES: Array<{ id: string; ar: string; en: string }> = [
  { id: 'beirut', ar: 'بيروت', en: 'Beirut' },
  { id: 'mount_lebanon', ar: 'جبل لبنان', en: 'Mount Lebanon' },
  { id: 'north', ar: 'الشمال', en: 'North Lebanon' },
  { id: 'akkar', ar: 'عكار', en: 'Akkar' },
  { id: 'bekaa', ar: 'البقاع', en: 'Bekaa' },
  { id: 'baalbek_hermel', ar: 'بعلبك-الهرمل', en: 'Baalbek-Hermel' },
  { id: 'south', ar: 'الجنوب', en: 'South Lebanon' },
  { id: 'nabatieh', ar: 'النبطية', en: 'Nabatieh' },
];

export function getGovernorateLabel(id: string, lang: string): string {
  const g = GOVERNORATES.find((g) => g.id === id);
  return g ? (lang === 'en' ? g.en : g.ar) : id;
}

export const HOUSING_TYPES: HousingType[] = ['apartment', 'house', 'studio', 'room', 'other'];
export const SECTS: Sect[] = ['sunni', 'shia', 'maronite', 'orthodox', 'druze', 'catholic', 'other', 'prefer_not_to_say'];
export const SEEKER_RELATIONS: SeekerRelation[] = ['family', 'extended', 'friends', 'individuals'];

/** Look up a region by id. */
export function getRegion(id: string): Region | undefined {
  return REGIONS.find((r) => r.id === id);
}

/** Resolve a region id to its localized label. Falls back to the id when unknown. */
export function getRegionLabel(id: string, lang: string): string {
  const r = getRegion(id);
  if (!r) return id;
  return lang === 'en' ? r.en : r.ar;
}

/** Sect labels (AR/EN) keyed by sect enum.
 *  Arabic variants are intentionally phrased with "من إخوانا" (from our brothers)
 *  to keep a respectful, inclusive tone — sensitive context in Lebanon. */
export const SECT_LABELS: Record<string, { ar: string; en: string }> = {
  sunni: { ar: 'من إخوانا السنّة', en: 'Sunni' },
  shia: { ar: 'من إخوانا الشيعة', en: 'Shia' },
  druze: { ar: 'من إخوانا الدروز', en: 'Druze' },
  maronite: { ar: 'من إخوانا الموارنة', en: 'Maronite' },
  orthodox: { ar: 'من إخوانا الأرثوذكس', en: 'Orthodox' },
  catholic: { ar: 'من إخوانا الكاثوليك', en: 'Catholic' },
  protestant: { ar: 'من إخوانا الإنجيليين', en: 'Protestant' },
  alawite: { ar: 'من إخوانا العلويين', en: 'Alawite' },
  other_christian: { ar: 'من إخوانا المسيحيين', en: 'Other Christian' },
  other_muslim: { ar: 'من إخوانا المسلمين', en: 'Other Muslim' },
  other: { ar: 'من إخوانا الآخرين', en: 'Other' },
  prefer_not: { ar: 'بفضّل ما جاوب', en: 'Prefer not to say' },
  prefer_not_to_say: { ar: 'بفضّل ما جاوب', en: 'Prefer not to say' },
};

/** Resolve a sect to its localized label. */
export function getSectLabel(sect: string, lang: string): string {
  const entry = SECT_LABELS[sect];
  if (!entry) return sect;
  return lang === 'en' ? entry.en : entry.ar;
}

/** Generic helper to resolve an enum id against a label map. Falls back to id. */
function resolveLabel(
  map: Record<string, { ar: string; en: string }>,
  id: string,
  lang: string,
): string {
  const entry = map[id];
  if (!entry) return id;
  return lang === 'en' ? entry.en : entry.ar;
}

/** Profession labels (AR/EN). */
export const PROFESSION_LABELS: Record<string, { ar: string; en: string }> = {
  student_school: { ar: 'طالب/ة مدرسة', en: 'Student (school)' },
  student_university: { ar: 'طالب/ة جامعي', en: 'University student' },
  employee: { ar: 'موظف/ة', en: 'Employee' },
  engineer: { ar: 'مهندس/ة', en: 'Engineer' },
  doctor: { ar: 'طبيب/ة', en: 'Doctor' },
  worker: { ar: 'عامل/ة', en: 'Worker' },
  merchant: { ar: 'تاجر/ة', en: 'Merchant' },
  retired: { ar: 'متقاعد/ة', en: 'Retired' },
  homemaker: { ar: 'ربّة منزل', en: 'Homemaker' },
  unemployed: { ar: 'بدون عمل', en: 'Not working' },
  other: { ar: 'آخر', en: 'Other' },
};
const PROFESSION_GENDERED: Record<string, { m: string; f: string }> = {
  student_school:    { m: 'طالب مدرسة',    f: 'طالبة مدرسة' },
  student_university:{ m: 'طالب جامعي',    f: 'طالبة جامعية' },
  employee:          { m: 'موظف',           f: 'موظفة' },
  engineer:          { m: 'مهندس',          f: 'مهندسة' },
  doctor:            { m: 'طبيب',           f: 'طبيبة' },
  worker:            { m: 'عامل',           f: 'عاملة' },
  merchant:          { m: 'تاجر',           f: 'تاجرة' },
  retired:           { m: 'متقاعد',         f: 'متقاعدة' },
  homemaker:         { m: 'ربّ منزل',       f: 'ربّة منزل' },
  other:             { m: 'آخر',            f: 'أخرى' },
};
export function getProfessionLabel(id: string, lang: string, gender?: 'male' | 'female'): string {
  if (lang === 'ar' && gender) {
    const g = PROFESSION_GENDERED[id];
    if (g) return gender === 'female' ? g.f : g.m;
  }
  return resolveLabel(PROFESSION_LABELS, id, lang);
}

/** Family relation labels. */
export const RELATION_LABELS: Record<string, { ar: string; en: string }> = {
  head_of_household: { ar: 'رب/ربّة العيلة', en: 'Head of household' },
  spouse: { ar: 'زوج/زوجة', en: 'Spouse' },
  child: { ar: 'ابن/ابنة', en: 'Child' },
  parent: { ar: 'أب/أم', en: 'Parent' },
  sibling: { ar: 'أخ/أخت', en: 'Sibling' },
  other: { ar: 'آخر', en: 'Other' },
};
export function getRelationLabel(id: string, lang: string): string {
  return resolveLabel(RELATION_LABELS, id, lang);
}

/** Marital status labels. */
export const MARITAL_LABELS: Record<string, { ar: string; en: string }> = {
  single: { ar: 'أعزب/عزباء', en: 'Single' },
  married: { ar: 'متزوج/ة', en: 'Married' },
  divorced: { ar: 'مطلّق/ة', en: 'Divorced' },
  widowed: { ar: 'أرمل/ة', en: 'Widowed' },
};
export function getMaritalLabel(id: string, lang: string): string {
  return resolveLabel(MARITAL_LABELS, id, lang);
}

/** Gender labels. */
export const GENDER_LABELS: Record<string, { ar: string; en: string }> = {
  male: { ar: 'ذكر', en: 'Male' },
  female: { ar: 'أنثى', en: 'Female' },
};
export function getGenderLabel(id: string, lang: string): string {
  return resolveLabel(GENDER_LABELS, id, lang);
}

/** Rental type labels. */
export const RENTAL_TYPE_LABELS: Record<string, { ar: string; en: string }> = {
  free: { ar: 'مجّاني', en: 'Free' },
  paid: { ar: 'مدفوع', en: 'Paid' },
};
export function getRentalTypeLabel(id: string, lang: string): string {
  return resolveLabel(RENTAL_TYPE_LABELS, id, lang);
}

/** Report reason labels. */
export const REPORT_REASON_LABELS: Record<string, { ar: string; en: string }> = {
  false_info: { ar: 'معلومات مش صحيحة', en: 'False information' },
  inappropriate: { ar: 'تصرّف مش لائق', en: 'Inappropriate behavior' },
  fraud: { ar: 'احتيال أو نصب', en: 'Fraud / scam' },
  threat: { ar: 'تهديد أو خطر', en: 'Threat / danger' },
  other: { ar: 'سبب تاني', en: 'Other' },
};
export function getReportReasonLabel(id: string, lang: string): string {
  return resolveLabel(REPORT_REASON_LABELS, id, lang);
}

/** Accessibility feature labels. Supports both API enum ids and UI short ids. */
export const ACCESSIBILITY_LABELS: Record<string, { ar: string; en: string }> = {
  wheelchair: { ar: 'كرسي متحرك', en: 'Wheelchair accessible' },
  accessible_bathroom: { ar: 'حمّام مجهّز', en: 'Accessible bathroom' },
  bathroom: { ar: 'حمّام مجهّز', en: 'Accessible bathroom' },
  ground_floor: { ar: 'طابق أرضي', en: 'Ground floor' },
  ground: { ar: 'طابق أرضي', en: 'Ground floor' },
  elevator: { ar: 'مصعد', en: 'Elevator' },
};
export function getAccessibilityLabel(id: string, lang: string): string {
  return resolveLabel(ACCESSIBILITY_LABELS, id, lang);
}

/** User role labels. */
export const ROLE_LABELS: Record<string, { ar: string; en: string }> = {
  host: { ar: 'مستضيف', en: 'Host' },
  seeker: { ar: 'عيلة بتدوّر على مأوى', en: 'Seeker' },
  admin: { ar: 'مشرف', en: 'Admin' },
};
export function getRoleLabel(id: string, lang: string): string {
  return resolveLabel(ROLE_LABELS, id, lang);
}
